import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { TR_MONTHS, TR_DAYS_SHORT, formatTime } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useInvitationViewer } from "@/contexts/InvitationViewerContext";
import { CalendarClock, MapPin, Share2, Image as ImageIcon } from "lucide-react";
import CategoryIcon from "@/components/CategoryIcon";
import ShareDialog, { buildEventShareText } from "@/components/ShareDialog";
import SocialMediaCard from "@/components/SocialMediaCard";

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysBetween = (a, b) => {
  const ms = 24 * 60 * 60 * 1000;
  return Math.round((b - a) / ms);
};

const UpcomingEvents = ({ events = [], daysAhead = 7 }) => {
  const { categoryMap } = useSettings();
  const { openInvitation } = useInvitationViewer();
  const [shareEvent, setShareEvent] = useState(null);

  const items = useMemo(() => {
    const today = startOfToday();
    const horizon = new Date(today);
    horizon.setDate(today.getDate() + daysAhead);
    return events
      .filter((ev) => {
        const d = new Date(ev.event_datetime);
        return !isNaN(d.getTime()) && d >= today && d < horizon;
      })
      .sort((a, b) => new Date(a.event_datetime) - new Date(b.event_datetime));
  }, [events, daysAhead]);

  if (items.length === 0) {
    return (
      <section className="rounded-2xl border border-dashed border-stone-300 bg-white/60 p-6 text-center animate-fade-up" data-testid="upcoming-empty">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
          <CalendarClock className="w-3.5 h-3.5" />
          Bu Hafta
        </div>
        <p className="text-sm text-[#4B5563] mt-1">Önümüzdeki 7 gün içinde onaylı etkinlik bulunmuyor.</p>
      </section>
    );
  }

  return (
    <section className="animate-fade-up" data-testid="upcoming-widget">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">
            <CalendarClock className="w-3.5 h-3.5" />
            Bu Hafta
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2937] mt-1">Yaklaşan Etkinlikler</h2>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8A252C]/10 text-[#8A252C] text-xs font-bold" data-testid="upcoming-count">
          {items.length} etkinlik
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((ev) => {
          const cat = categoryMap[ev.category];
          const d = new Date(ev.event_datetime);
          const today = startOfToday();
          const eventDay = new Date(d);
          eventDay.setHours(0, 0, 0, 0);
          const delta = daysBetween(today, eventDay);
          const badge = delta === 0 ? "Bugün" : delta === 1 ? "Yarın" : `${delta} gün sonra`;
          return (
            <article key={ev.id} data-testid={`upcoming-item-${ev.id}`}
              className="group relative bg-white rounded-2xl border border-stone-200 shadow-sm p-4 sm:p-5 hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden">
              <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: cat?.accent }} />
              <div className="flex items-start gap-3">
                <div className="w-14 h-16 rounded-xl flex flex-col items-center justify-center shrink-0"
                  style={{ backgroundColor: `${cat?.accent}12`, border: `1px solid ${cat?.accent}30` }}>
                  <div className="font-display text-2xl font-bold leading-none" style={{ color: cat?.accent }}>
                    {String(d.getDate()).padStart(2, "0")}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-[#4B5563] mt-0.5">
                    {TR_MONTHS[d.getMonth()].slice(0, 3)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-lg flex items-center justify-center text-sm"
                      style={{ backgroundColor: `${cat?.accent}15`, color: cat?.accent }}>
                      <CategoryIcon icons={cat?.icons} />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: cat?.accent }}>{cat?.label}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#8A252C] font-bold whitespace-nowrap"
                      data-testid={`upcoming-badge-${ev.id}`}>
                      {badge}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#1F2937] mt-1.5 leading-tight truncate">{ev.whose_event}</h3>
                  <div className="text-xs text-[#4B5563] mt-0.5 truncate">{ev.families}</div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-[#4B5563]">
                    <span className="inline-flex items-center gap-1 font-semibold text-[#1F2937]">
                      <CalendarClock className="w-3.5 h-3.5 text-[#8A252C]" />
                      {TR_DAYS_SHORT[(d.getDay() + 6) % 7]} · {formatTime(ev.event_datetime)}
                    </span>
                    <span className="inline-flex items-center gap-1 truncate">
                      <MapPin className="w-3.5 h-3.5 text-[#8A252C]" />
                      <span className="truncate">{ev.venue}</span>
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button
                      onClick={() => setShareEvent(ev)}
                      data-testid={`upcoming-share-${ev.id}`}
                      variant="outline"
                      size="sm"
                      className="border-stone-300 gap-1.5 h-8"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      Paylaş
                    </Button>
                    {ev.invitation_url && (
                      <button
                        type="button"
                        onClick={() => openInvitation(ev.invitation_url)}
                        data-testid={`upcoming-invitation-${ev.id}`}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#D4AF37]/60 text-[#8A252C] hover:bg-[#D4AF37]/10 text-sm font-medium"
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                        Davetiyeyi Gör
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {shareEvent && (
        <ShareDialog
          open={!!shareEvent}
          onOpenChange={(o) => !o && setShareEvent(null)}
          title="Etkinliği Paylaş"
          subtitle="Görseli sosyal medyada paylaşabilir veya PNG olarak indirebilirsiniz."
          fileNamePrefix={`sahder-${shareEvent.category}`}
          buildText={() => buildEventShareText(shareEvent, categoryMap)}
        >
          {(ref) => <SocialMediaCard ref={ref} event={shareEvent} />}
        </ShareDialog>
      )}
    </section>
  );
};

export default UpcomingEvents;
